'use client';

import * as React from 'react';
import type { CultivDash, CultivPipe } from '../types';
import { MARKETING_CDN } from '../site';
import { MockDefs, MockIcon, MockSidebar } from './DashboardMock';
import './pipeline-mock.css';

/* Scene-1 widget→pipeline loop — the phone widget qualifies a lead, then focus
   shifts to the pipeline board where the card drops into "Nieuwe leads".
   Re-choreographed from the handoff (approach A): the board is full-canvas the
   whole loop, muted behind the phone in act 1; at 70-76% board and phone swap
   mute states instead of the phone sliding out. Fixed 1220×640 canvas in the
   standard .scene-canvas frame. Names/addresses/phones are shared fixture data
   (not localized); all UI strings come from copy.pipe / copy.dash. */

const LOGO = `${MARKETING_CDN}/cultiv-symbol-orange.svg`;

/* extra lucide symbols on top of MockDefs (shared s4d-* set) */
function PipeDefs() {
  const p = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;
  return (
    <svg width="0" height="0" style={{ position: 'absolute', overflow: 'hidden' }} aria-hidden="true">
      <defs>
        <symbol id="s1d-x" viewBox="0 0 24 24" {...p}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></symbol>
        <symbol id="s1d-check-circle" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></symbol>
        <symbol id="s1d-star" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.958a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.367 2.446a1 1 0 00-.364 1.118l1.287 3.958c.3.922-.755 1.688-1.539 1.118l-3.366-2.446a1 1 0 00-1.176 0l-3.366 2.446c-.784.57-1.838-.196-1.539-1.118l1.287-3.958a1 1 0 00-.364-1.118L2.063 9.385c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.951-.69l1.285-3.958z" /></symbol>
        <symbol id="s1d-map-pin" viewBox="0 0 24 24" {...p}><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" /></symbol>
        <symbol id="s1d-phone" viewBox="0 0 24 24" {...p}><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" /></symbol>
        <symbol id="s1d-mail" viewBox="0 0 24 24" {...p}><path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" /><rect x="2" y="4" width="20" height="16" rx="2" /></symbol>
        <symbol id="s1d-clock" viewBox="0 0 24 24" {...p}><path d="M12 6v6l4 2" /><circle cx="12" cy="12" r="10" /></symbol>
        <symbol id="s1d-users" viewBox="0 0 24 24" {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><path d="M16 3.128a4 4 0 0 1 0 7.744" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><circle cx="9" cy="7" r="4" /></symbol>
        <symbol id="s1d-banknote" viewBox="0 0 24 24" {...p}><rect width="20" height="12" x="2" y="6" rx="2" /><circle cx="12" cy="12" r="2" /><path d="M6 12h.01M18 12h.01" /></symbol>
        <symbol id="s1d-more-vertical" viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></symbol>
        <symbol id="s1d-chevron-right" viewBox="0 0 24 24" {...p}><path d="m9 18 6-6-6-6" /></symbol>
        <symbol id="s1d-layout-grid" viewBox="0 0 24 24" {...p}><rect width="7" height="7" x="3" y="3" rx="1" /><rect width="7" height="7" x="14" y="3" rx="1" /><rect width="7" height="7" x="14" y="14" rx="1" /><rect width="7" height="7" x="3" y="14" rx="1" /></symbol>
        <symbol id="s1d-import" viewBox="0 0 24 24" {...p}><path d="M12 3v12" /><path d="m8 11 4 4 4-4" /><path d="M8 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-4" /></symbol>
        <symbol id="s1d-plus" viewBox="0 0 24 24" {...p}><path d="M5 12h14" /><path d="M12 5v14" /></symbol>
        <symbol id="s1d-arrow-down-up" viewBox="0 0 24 24" {...p}><path d="m3 16 4 4 4-4" /><path d="M7 20V4" /><path d="m21 8-4-4-4 4" /><path d="M17 4v16" /></symbol>
        <symbol id="s1d-filter-lines" viewBox="0 0 24 24" {...p}><path d="M4 6h16" /><path d="M7 12h10" /><path d="M10 18h4" /></symbol>
        <symbol id="s1d-alarm-clock" viewBox="0 0 24 24" {...p}><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2 2" /><path d="M5 3 2 6" /><path d="m22 6-3-3" /><path d="M6.38 18.7 4 21" /><path d="M17.64 18.67 20 21" /></symbol>
      </defs>
    </svg>
  );
}

function PIcon({ id, s = 12 }: { id: string; s?: number }) {
  return (
    <svg width={s} height={s}>
      <use href={`#s1d-${id}`} />
    </svg>
  );
}

/* shared fixture data — identical across locales (names, addresses, numbers) */
type Lead = { name: string; addr: string; phone: string; mail?: string; timeIdx: number; ava: string };
const LANES: Lead[][] = [
  [], // Nieuwe leads — drop lane, rendered separately
  [
    { name: 'Élodie Moreau', addr: 'Kesselsesteenweg 74, 2560 Nijlen', phone: '+32 400 01 21 38', mail: 'elodie.moreau@gmail.com', timeIdx: 0, ava: 'MR' },
    { name: 'Wouter Declercq', addr: 'Salvatorbaan 9, 2560 Nijlen', phone: '+32 400 02 42 70', timeIdx: 1, ava: 'MR' },
  ],
  [{ name: 'Camille Dubois', addr: 'Rue Bout de La-Haut 53, 5651 Walcourt', phone: '+32 477 31 32 61', timeIdx: 2, ava: 'SB' }],
  [
    { name: 'Ruben Verbeke', addr: 'Ruiterweg 146, 1780 Wemmel', phone: '+32 400 09 12 17', mail: 'ruben.verbeke@gmail.com', timeIdx: 3, ava: 'MR' },
    { name: 'Mathilde Delvaux', addr: 'Hubert Frere-Orbanlaan 1, 9000 Gent', phone: '+32 400 02 30 17', timeIdx: 4, ava: 'LB' },
  ],
  [
    { name: 'Stijn Vermeersch', addr: 'Rue François Lebon 46, 1400 Nivelles', phone: '+32 457 12 98 34', timeIdx: 5, ava: 'MR' },
    { name: 'Antoine Charlier', addr: 'Rue de la Station 27, 1300 Wavre', phone: '+32 471 95 65 85', timeIdx: 6, ava: 'SB' },
  ],
  [{ name: 'Femke Dewinter', addr: 'Place Marie-José 9, 1050 Ixelles', phone: '+32 478 04 04 04', mail: 'femke.dewinter@hotmail.com', timeIdx: 7, ava: 'LB' }],
];
const NEW_LEAD: Lead = { name: 'Emma Janssens', addr: 'Avenue de Mai 118, 1200 Woluwe', phone: '+32 400 099 610', timeIdx: -1, ava: 'EJ' };
const STEP_WIDTHS = ['12%', '40%', '65%', '100%'];

function LeadCard({ lead, time, makeRoom }: { lead: Lead; time: string; makeRoom?: boolean }) {
  return (
    <div className={'s1d-card' + (makeRoom ? ' s1d-card--room' : '')}>
      <div className="s1d-card-top">
        <p>{lead.name}</p>
        <span className="s1d-card-src">
          {/* eslint-disable-next-line @next/next/no-img-element -- 12px badge in a scaled canvas */}
          <img src="/cultiv/favicon.svg" width={12} height={12} alt="" />
        </span>
      </div>
      <div className="s1d-card-line"><PIcon id="map-pin" /><span>{lead.addr}</span></div>
      <div className="s1d-card-line"><PIcon id="phone" /><span className="num">{lead.phone}</span></div>
      <div className="s1d-card-line"><PIcon id="mail" />{lead.mail ? <span>{lead.mail}</span> : <span>—</span>}</div>
      <div className="s1d-card-foot">
        <span className="when"><span><PIcon id="clock" s={11} /><span className="kq-cap">{time}</span></span></span>
        <span className="ava">{lead.ava}</span>
      </div>
    </div>
  );
}

function LaneHead({
  color,
  name,
  leads,
  amount,
  tickLeads,
  tickAmount,
  sla,
}: {
  color: string;
  name: string;
  leads: string;
  amount: string;
  tickLeads?: string;
  tickAmount?: string;
  sla?: string;
}) {
  return (
    <div className="s1d-head">
      <div className="s1d-head-in">
        <div className="s1d-head-row">
          <div className="s1d-head-id">
            <span className="dot" style={{ background: color }} />
            <span className="nm kq-cap">{name}</span>
            {sla && <span className="sla"><PIcon id="alarm-clock" /><span className="kq-cap">{sla}</span></span>}
          </div>
          <button type="button" className="more"><PIcon id="more-vertical" s={16} /></button>
        </div>
        <div className="s1d-head-meta">
          <p>
            <span className="pair">
              <PIcon id="users" />
              {tickLeads ? (
                <span className="s1d-tickbox"><span><b>{leads}</b><b>{tickLeads}</b></span></span>
              ) : (
                <span className="num kq-cap">{leads}</span>
              )}
            </span>
            <span className="sep">·</span>
            <span className="pair">
              <PIcon id="banknote" />
              {tickAmount ? (
                <span className="s1d-tickbox"><span><b>{amount}</b><b>{tickAmount}</b></span></span>
              ) : (
                <span className="num kq-cap">{amount}</span>
              )}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export function PipelineMock({ dash, pipe }: { dash: CultivDash; pipe: CultivPipe }) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(0);

  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    // offsetWidth = layout width, immune to ancestor reveal-transforms (see DashboardMock)
    const fit = () => setScale(el.offsetWidth / 1220);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const stages = dash.stages;
  const laneColors = ['#2563eb', '#000000', '#a855f7', '#ef4444', '#3e7c4f'];

  return (
    <div ref={rootRef} className="scene-canvas s1d" aria-hidden="true">
      <MockDefs />
      <PipeDefs />
      <div className="s1d-canvas" style={{ transform: `scale(${scale})`, visibility: scale ? 'visible' : 'hidden' }}>

        {/* ── pipeline board (muted in act 1, sharp in act 2) ── */}
        <div className="s1d-board">
          <div className="s1d-board-in">
            <MockSidebar dash={dash} active="pipeline" pipelineCount="149" />
            <main className="s1d-main">
              <div className="s1d-bar">
                <nav className="s1d-crumbs">
                  <span className="s1d-collapse">
                    <i>
                      <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                    </i>
                  </span>
                  <span className="root kq-cap">{dash.sideWorkspace}</span>
                  <PIcon id="chevron-right" s={14} />
                  <span className="page"><b className="kq-cap">{dash.sidePipeline}</b><MockIcon id="chevron-down" s={14} /></span>
                </nav>
                <div className="s1d-actions">
                  <button type="button" className="s1d-abtn">
                    <PIcon id="layout-grid" s={15} /><span className="kq-cap">{pipe.funnelBtn}</span><MockIcon id="chevron-down" s={14} />
                  </button>
                  <div className="s1d-group-ctl">
                    <button type="button" className="lab">
                      <span className="kq-cap">{pipe.groupBy}</span><span className="v kq-cap">{pipe.groupByVal}</span><MockIcon id="chevron-down" s={14} />
                    </button>
                    <button type="button" className="x"><PIcon id="x" /></button>
                  </div>
                  <button type="button" className="s1d-abtn icon"><PIcon id="import" s={15} /></button>
                  <button type="button" className="s1d-new"><PIcon id="plus" s={15} /><span className="kq-cap">{pipe.newLead}</span></button>
                </div>
              </div>
              <div className="s1d-filters">
                <div className="s1d-search"><MockIcon id="search" s={13} /><span>{pipe.search}</span></div>
                <div className="s1d-seg">
                  <button type="button" className="on"><span className="kq-cap">{pipe.allTab}</span></button>
                  <button type="button"><span className="kq-cap">{pipe.myTab}</span></button>
                </div>
                <button type="button" className="s1d-fbtn"><PIcon id="arrow-down-up" s={13} /><span className="kq-cap">{pipe.sort}</span></button>
                <button type="button" className="s1d-fbtn"><PIcon id="filter-lines" s={13} /><span className="kq-cap">{pipe.filter}</span></button>
              </div>
              <div className="s1d-body">
                <div className="s1d-lanes-bg">{Array.from({ length: 6 }, (_, i) => <i key={i} />)}</div>
                <div className="s1d-heads">
                  <LaneHead color={laneColors[0]} name={stages[0]} leads={pipe.colNewBefore} amount={pipe.colNewAmtBefore} tickLeads={pipe.colNewAfter} tickAmount={pipe.colNewAmtAfter} />
                  <LaneHead color={laneColors[1]} name={stages[1]} leads={pipe.colVoiceLeads} amount={pipe.colVoiceAmt} sla={pipe.colVoiceChip} />
                  <LaneHead color={laneColors[2]} name={stages[2]} leads={pipe.colContactedLeads} amount={pipe.colContactedAmt} />
                  <LaneHead color={laneColors[3]} name={stages[3]} leads={pipe.colVisitLeads} amount={pipe.colVisitAmt} />
                  <LaneHead color={laneColors[4]} name={stages[4]} leads={pipe.colMandatesLeads} amount={pipe.colMandatesAmt} />
                </div>
                <div className="s1d-groupbar">
                  <MockIcon id="chevron-down" s={12} />
                  {/* eslint-disable-next-line @next/next/no-img-element -- 16px badge in a scaled canvas */}
                  <img src="/cultiv/favicon.svg" width={16} height={16} alt="" />
                  <span className="nm kq-cap">Cultiv</span>
                  <span className="cnt"><span className="s1d-tickbox"><span><b>{pipe.groupCountBefore}</b><b>{pipe.groupCountAfter}</b></span></span></span>
                </div>
                <div className="s1d-lanes">
                  <div className="s1d-lane s1d-lane--drop">
                    <div className="s1d-dropslot" />
                    {LANES[1].map((lead) => (
                      <LeadCard key={lead.name} lead={lead} time={pipe.times[lead.timeIdx]} makeRoom />
                    ))}
                  </div>
                  {LANES.slice(2).map((lane, i) => (
                    <div className="s1d-lane" key={i}>
                      {lane.map((lead) => (
                        <LeadCard key={lead.name} lead={lead} time={pipe.times[lead.timeIdx]} />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </main>
          </div>
        </div>

        {/* ── phone widget (sharp in act 1, dissolves at the hand-off) ── */}
        <div className="s1d-phone">
          <div className="s1d-case">
            <span className="s1d-btn s1d-btn--vol1" />
            <span className="s1d-btn s1d-btn--vol2" />
            <span className="s1d-btn s1d-btn--pow" />
            <div className="s1d-screen">
              <div className="s1d-ph-in">
                <div className="s1d-wh">
                  <button type="button" className="s1d-wh-close"><PIcon id="x" s={17.6} /></button>
                  <div className="s1d-wh-row">
                    <div className="s1d-wh-ava">
                      {/* eslint-disable-next-line @next/next/no-img-element -- decorative mark in a scaled canvas */}
                      <img src={LOGO} alt="" />
                    </div>
                    <p>{pipe.widgetTitle}</p>
                  </div>
                  <div className="s1d-steps">
                    {pipe.steps.map((label, i) => (
                      <div className="s1d-step" key={i}>
                        <div className="track"><i style={{ width: STEP_WIDTHS[i] }} /></div>
                        <div className="chip">{label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="s1d-feed">
                  <div className="s1d-msgs">

                    <div className="s1d-row s1d-a-r1">
                      <div className="s1d-ba">
                        <p>{pipe.msgIntro}</p>
                        <span className="s1d-cta-wrap">
                          <button type="button" className="s1d-cta" style={{ animation: 's1d-press1 31s linear infinite both' }}>{pipe.yes}</button>
                        </span>
                      </div>
                      <div className="s1d-meta">{pipe.meta}</div>
                    </div>

                    <div className="s1d-row s1d-row--user s1d-a-r2">
                      <div className="s1d-bu">{pipe.yes}</div>
                    </div>

                    <div className="s1d-row s1d-a-r3">
                      <div className="s1d-ba">
                        <p>{pipe.msgQuestions}</p>
                        <p className="sep">{pipe.msgAddress}</p>
                        <div className="s1d-inp" style={{ animation: 's1d-focus 31s linear infinite both' }}>
                          <span className="ph" style={{ animation: 's1d-ph 31s linear infinite both' }}>{pipe.addrPlaceholder}</span>
                          <span className="typed" style={{ animation: 's1d-type 31s steps(44,end) infinite both' }}>{NEW_LEAD.addr}</span>
                          <span className="caret" style={{ animation: 's1d-caret 31s linear infinite both' }}><i /></span>
                        </div>
                        <div className="s1d-a-r4">
                          <p style={{ margin: '12px 0 8px' }}>{pipe.msgType}</p>
                          <div className="s1d-chiprow">
                            <label className="s1d-chip"><span>{pipe.optApartment}</span></label>
                            <label className="s1d-chip s1d-chip--pick">
                              <span>{pipe.optHouse} <span className="chk"><PIcon id="check-circle" s={16} /></span></span>
                            </label>
                          </div>
                          <span className="s1d-cta-wrap" style={{ marginTop: 16 }}>
                            <button type="button" className="s1d-cta s1d-cta--md" style={{ animation: 's1d-press2 31s linear infinite both' }}>{pipe.cont}</button>
                          </span>
                        </div>
                      </div>
                      <div className="s1d-meta">{pipe.meta}</div>
                    </div>

                    <div className="s1d-row s1d-row--user s1d-a-r5">
                      <div className="s1d-bu s1d-bu--multi">
                        {pipe.infoIntro}
                        <div>• {pipe.infoAddress}: {NEW_LEAD.addr}</div>
                        <div>• {pipe.infoType}: {pipe.optHouse}</div>
                      </div>
                    </div>

                    <div className="s1d-row s1d-a-typ">
                      <div className="s1d-typing">
                        <em>{pipe.calc}</em>
                        <span className="dots"><i /><i /><i /></span>
                      </div>
                    </div>

                    <div className="s1d-row s1d-a-r6">
                      <div className="s1d-ba">
                        <p>{pipe.msgSold}</p>
                        <div className="s1d-map">
                          {/* eslint-disable-next-line @next/next/no-img-element -- fixture image in a scaled canvas */}
                          <img src="/cultiv/scene1-map.png" alt={pipe.mapAlt} />
                        </div>
                        <span className="s1d-cta-wrap" style={{ marginTop: 14 }}>
                          <button type="button" className="s1d-cta s1d-cta--md" style={{ animation: 's1d-press3 31s linear infinite both' }}>{pipe.cont}</button>
                        </span>
                      </div>
                    </div>

                    <div className="s1d-row s1d-row--user s1d-a-r6b">
                      <div className="s1d-bu s1d-bu--multi">{pipe.askValue}</div>
                    </div>

                    <div className="s1d-row s1d-a-r7">
                      <div className="s1d-ba">
                        <p className="tight">{pipe.msgResult}</p>
                        <div className="s1d-price">
                          <p className="amt">{pipe.priceRange}</p>
                          <div className="acc">
                            <em>{pipe.accuracy}</em>
                            <span className="stars">
                              {Array.from({ length: 5 }, (_, i) => <PIcon key={i} id="star" s={16} />)}
                            </span>
                          </div>
                        </div>
                        <p className="s1d-note">{pipe.contactNote}</p>
                        <div className="s1d-a-r7b">
                          <p className="sep" style={{ margin: '12px 0 8px' }}>{pipe.msgContact}</p>
                          <div className="s1d-inp" style={{ animation: 's1d-focus-nm 31s linear infinite both' }}>
                            <span className="ph" style={{ animation: 's1d-ph-nm 31s linear infinite both' }}>{pipe.namePh}</span>
                            <span className="typed" style={{ animation: 's1d-type-nm 31s steps(20,end) infinite both' }}>{NEW_LEAD.name}</span>
                            <span className="caret" style={{ animation: 's1d-caret-nm 31s linear infinite both' }}><i /></span>
                          </div>
                          <div className="s1d-inp" style={{ marginTop: 8, animation: 's1d-focus-ph 31s linear infinite both' }}>
                            <span className="ph" style={{ animation: 's1d-ph-ph 31s linear infinite both' }}>{pipe.phonePh}</span>
                            <span className="typed" style={{ animation: 's1d-type-ph 31s steps(20,end) infinite both' }}>{NEW_LEAD.phone}</span>
                            <span className="caret" style={{ animation: 's1d-caret-ph 31s linear infinite both' }}><i /></span>
                          </div>
                          <span className="s1d-cta-wrap" style={{ marginTop: 16 }}>
                            <button type="button" className="s1d-cta s1d-cta--md" style={{ animation: 's1d-press4 31s linear infinite both' }}>{pipe.send}</button>
                          </span>
                        </div>
                      </div>
                      <div className="s1d-meta">{pipe.meta}</div>
                    </div>

                  </div>
                </div>

                <div className="s1d-pow"><span>{pipe.poweredBy}</span></div>
                <div className="s1d-mouse">
                  <div className="drift">
                    <div className="click">
                      <svg viewBox="0 0 24 24" width="26" height="26"><path d="M5 2.5 L5 18.2 L9.1 14.4 L11.6 20.6 L14.4 19.4 L11.9 13.3 L17.4 13.3 Z" fill="#282828" stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" /></svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── the flying lead card ── */}
        <div className="s1d-fly">
          <div className="s1d-fly-dot" />
          <div className="s1d-fly-lift">
            <LeadCard lead={NEW_LEAD} time={pipe.justNow} />
          </div>
        </div>

      </div>
    </div>
  );
}
